import { isImageItem } from '@lib/utils/fileExtensions';
import { formatDurationToString, SECONDS_IN_HOUR } from '@lib/utils/units';

/**
 * The least atlas charges credits for one media's ingest, in seconds: a still image or a 2 s clip
 * costs what 5 s does. Applied to each media, never to a batch total. Credits only: storage counts
 * each media's real length (see `durationSeconds`).
 * Mirrors `MIN_INGEST_DURATION_SECONDS`, applied per media by `billForMediaIngest`:
 * https://github.com/ijw-fyi/external.api.atlas/blob/19faa637bf383462aa542629886c1d53e0db936e/src/modules/monetization/credits/creditsTranslationTable.ts#L2
 * https://github.com/ijw-fyi/external.api.atlas/blob/19faa637bf383462aa542629886c1d53e0db936e/src/modules/monetization/logic.ts#L659-L674
 */
export const MIN_INGEST_DURATION_SECONDS = 5;

/** Mirrors atlas `creditsTranslationTable['sz.ingest']` (same file as above, #L18). */
export const INGEST_CREDITS_PER_HOUR = 10000;

/** What ingesting a selection will cost, estimated before upload. */
export interface IngestEstimate {
  /**
   * Playable length of the items that have one; images add none. A lower bound when
   * `hasUnknownDuration`. Also the storage the selection takes: atlas checks and sums storage by
   * each media's real length, with no minimum, so a still image takes ~0.
   * https://github.com/ijw-fyi/external.api.atlas/blob/19faa637bf383462aa542629886c1d53e0db936e/src/modules/monetization/logic.ts#L575-L580
   * https://github.com/ijw-fyi/external.api.atlas/blob/19faa637bf383462aa542629886c1d53e0db936e/src/modules/library/dbs/mongo.ts#L139
   */
  durationSeconds: number;
  imageCount: number;
  /** Some item that isn't an image has no known length. It is priced at the minimum. */
  hasUnknownDuration: boolean;
  /** Credits the ingest charges: each item's length floored at the minimum, rounded up. */
  credits: number;
}

/**
 * Prices each item the way atlas bills it, then sums. Summing lengths first and pricing the total
 * once undercounts every item below the minimum: 200 photos would come to ~20 credits instead
 * of 2800.
 *
 * Known gap: an animated GIF, APNG or WebP is billed for its real length, which nothing here can
 * read before upload (MediaInfo reports none), so it is priced at the minimum like a still image.
 * It can close once stable Safari ships WebCodecs `ImageDecoder`.
 */
export function estimateIngest(
  items: readonly { duration?: number; name?: string; type?: string }[],
): IngestEstimate {
  const estimate: IngestEstimate = {
    durationSeconds: 0,
    imageCount: 0,
    hasUnknownDuration: false,
    credits: 0,
  };
  for (const item of items) {
    let seconds = 0;
    if (isImageItem(item)) {
      estimate.imageCount++;
    } else if (!item.duration) {
      // A 0 is a missing value, not a length: a multi-item unfurl keeps an empty duration column
      // as 0 where the single-item path maps it to undefined.
      estimate.hasUnknownDuration = true;
    } else {
      seconds = item.duration;
      estimate.durationSeconds += seconds;
    }
    const billed = Math.max(seconds, MIN_INGEST_DURATION_SECONDS);
    // Same expression as atlas, so floating point rounds the same way.
    estimate.credits += Math.ceil((billed / SECONDS_IN_HOUR) * INGEST_CREDITS_PER_HOUR);
  }
  return estimate;
}

/** How a selection's playable length reads. `minimum`: some lengths are unknown; the total is a floor. */
export interface DurationSummary {
  kind: 'exact' | 'minimum' | 'unknown';
  /** The length, or "Unknown". Carries no "≥": `formatDurationSummary` adds it. */
  text: string;
}

/**
 * Decides how a selection's length reads, so every surface says the same thing. Undefined for a
 * selection of only images, which is described by its image count instead of "0 sec".
 */
export function summarizeDuration({
  durationSeconds,
  hasUnknownDuration,
  imageCount,
}: IngestEstimate): DurationSummary | undefined {
  const text = formatDurationToString(durationSeconds, { precision: 0, detail: 2 });
  if (hasUnknownDuration) {
    return durationSeconds > 0 ? { kind: 'minimum', text } : { kind: 'unknown', text: 'Unknown' };
  }
  return durationSeconds > 0 || imageCount === 0 ? { kind: 'exact', text } : undefined;
}

export function formatDurationSummary({ kind, text }: DurationSummary): string {
  return kind === 'minimum' ? `≥ ${text}` : text;
}
